import { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import styled, { css, keyframes } from 'styled-components';
import tw from 'twin.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMapMarkerAlt, faCalendarAlt, faCheck, faRoad } from '@fortawesome/free-solid-svg-icons';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, gql } from '@apollo/client';
import { CANCEL_BOOKING } from '../../../services/bookingService/queries';
import { toast } from 'react-toastify';
import { useLocation, useParams } from 'react-router-dom';
import { GET_CAR } from '../../../services/carService/queries';
import { carAsRented } from '../../../utils/carTripSettings';
import { IGetCarQuery } from '../../../services/carService/__generated__/queries.generated';
import { GET_LOCATION } from '../../../services/locationServices/queries';
import {
  IGetLocationQuery,
  IGetLocationQueryVariables,
} from '../../../services/locationServices/__generated__/queries.generated';
import { format } from 'date-fns';
import {
  CREATE_SETUP_INTENT_FOR_BOOKING,
  CREATE_DEPOSIT_PAYMENT_INTENT,
  CREATE_RENTAL_PAYMENT_INTENT,
} from '../../../services/stripeService/queries';
import { describeCheckoutPaymentError } from './checkoutPaymentError';
import {
  CAR_NOT_BOOKABLE,
  graphqlErrorCode,
  graphqlErrorMessage,
} from '../../../utils/listingHold';
import { useStripePromise } from '../../../hooks/useStripePromise';
import { useBookingLocale } from '../../../hooks/useBookingLocale';
import { checkoutAllowanceText } from '../../../utils/tripDistance';
import { Elements } from '@stripe/react-stripe-js';
import { PaymentForm } from './PaymentForm';
import { useBookingsClosedPrompt } from '../../../hooks/useBookingsOpen';
import { BookingsClosedModal } from '../../../components/BookingsClosedModal';
import {
  useGetBookingQuery,
  useCheckCarAvailabilityMutation,
  useUpdateBookingProtectionPlanMutation,
  useCreateBookingIntentMutation,
} from '../../../services/bookingService/__generated__/queries.generated';
import { CREATE_BOOKING_INTENT } from '../../../services/bookingService/queries';
import {
  ICancelBookingMutation,
  ICancelBookingMutationVariables,
} from '../../../services/bookingService/__generated__/queries.generated';
import { IProtectionPlan, BookingStatus, ErrorCode } from '../../../../__generated__/graphql';
import { URLS } from '../../../../constants/urls';
import { getBrand } from '../../../constants/brands';
import { useCheckoutPolicies } from '../../../hooks/useCheckoutPolicies';
import {
  isPolicyVersionOutdated,
  outdatedPolicyMessage,
} from '../../../services/policyService/checkoutPolicies';
import { ChooseProtectionPlan } from '../../../components/protectionPlan/chooseProtectionPlan';
import { CheckoutPageSkeleton } from '../../../components/skeletons';
import { useNavigate } from 'react-router-dom';
import ChargingDetails from '../../../components/chargingDetails';
import { currencyCodeSuffix } from '../../../utils/money';
import _ from 'lodash';
import { toastActions } from '../../../../store/toastStore';
import { VALIDATE_COUPON } from '../../../services/couponService/queries';
import { useLazyQuery } from '@apollo/client';
import { authStore } from '../../../../store/authStore';
import { useSnapshot } from 'valtio';
import { PhoneVerification } from '../../../components/PhoneVerification';
import { Colors } from '../../../styles/colors';
import LoadingOverlay from '../../../components/LoadingOverlay';
import { modalActions } from '../../../../store/authStore/modalStore';
import {
  clearReferralMarker,
  getReferralMarkerForBooking,
} from '../../../utils/referralAttribution';

const isAuthRequiredError = (error: any) => {
  const graphQLErrors = error?.graphQLErrors ?? [];
  return (
    graphQLErrors.some(
      (graphQLError: any) => graphQLError?.extensions?.code === ErrorCode.NoTokenProvided,
    ) ||
    /no token|unauthorized|not authenticated|user not authenticated/i.test(error?.message ?? '')
  );
};

const PageContainer = styled.div`
  ${tw`
    flex
    flex-col
    md:flex-row
    w-full
    max-w-full
    min-w-0
    max-w-screen-xl
    mx-auto
    px-4
    md:px-6
    lg:px-0
    pt-4
    md:pt-8
    pb-24
    md:pb-16
    gap-8
    box-border
    overflow-x-hidden
  `}
`;

const LeftSection = styled.div`
  ${tw`
    w-full
    md:w-2/3 max-w-full min-w-0 box-border`}
  @media (max-width: 767px) {
    display: contents;
  }
`;

const RightSection = styled.div`
  ${tw`
    w-full
    md:w-1/3
  `}
  @media (max-width: 767px) {
    display: contents;
  }
`;

const CheckoutTitle = styled.h1`
  ${tw`
    text-2xl
    font-bold
    mb-6
    order-1
  `}
`;

const ProtectionSection = styled.div`
  ${tw`
    border
    border-border-default
    rounded-lg
    p-4
    mb-6
    order-3
  `}
`;

const ProtectionTitle = styled.h2`
  ${tw`
    text-lg
    font-semibold
    mb-4
  `}
`;

const ProtectionDescription = styled.p`
  ${tw`
    text-text-secondary
    mb-4
  `}
`;

const ProtectionOption = styled.div<{ isSelected: boolean }>`
  ${tw`
    border-2
    rounded-lg
    p-4
    mb-4
    cursor-pointer
    transition-all
    duration-200
  `}
  ${({ isSelected }) =>
    isSelected
      ? css`
          border-color: ${Colors.Black};
        `
      : tw`border-border-default`}

  ${({ isSelected }) =>
    !isSelected &&
    css`
      &:hover {
        ${tw`border-border-default`}
      }
    `}
`;

const ProtectionDetails = styled.div`
  ${tw`mt-2 text-sm space-y-1`}
`;

const CarSummary = styled.div`
  ${tw`
    flex
    gap-4
    mb-6
  `}
`;

const CarImage = styled.img`
  ${tw`
    w-full
    h-24
    rounded-lg
    object-cover
  `}
`;

const CarDetails = styled.div`
  ${tw`flex flex-col`}
`;

const LocationInfo = styled.div`
  ${tw`
    flex
    items-center
    gap-2
    text-text-secondary
    mt-2
    italic
  `}
`;

const PriceBreakdown = styled.div`
  ${tw`
    border-t
    border-border-default
    mt-4
    pt-4
  `}
`;

const PriceRow = styled.div<{ isOdd: boolean }>`
  ${tw`
    flex
    items-center
    justify-between
    mb-2
    p-2
    rounded-md
  `}
  ${props => (props.isOdd ? tw`bg-bg-secondary` : tw`bg-white`)}
`;

const PriceLabel = styled.span`
  ${tw`
    text-text-secondary
    flex-1
  `}
`;

const PriceDetails = styled.div`
  ${tw`
    text-right
    flex-1
  `}
`;

const SmallText = styled.span`
  ${tw`
    text-sm
    text-text-secondary
    mt-1
  `}
`;

const Value = styled.span`
  ${tw`
    font-medium
  `}
`;

const TotalLabel = styled.span`
  ${tw`
    font-semibold
  `}
`;

const TotalValue = styled.span`
  ${tw`
    font-bold
  `}
`;

const BookButton = styled.button`
  ${tw`
    w-full
    text-white
    py-3
    px-6
    rounded-card
    font-semibold
    transition-colors
    cursor-pointer
  `}
  background-color: ${Colors.Black};
  &:hover {
    background-color: ${Colors.BlackHover};
  }
`;

const DesktopBookButton = styled(BookButton)`
  ${tw`
    hidden
    md:block
    mt-6
  `}
`;

const FixedButtonContainer = styled.div`
  ${tw`
    fixed
    bottom-0
    left-0
    right-0
    w-full
    max-w-full
    box-border
    bg-white
    px-4
    py-3
    border-t
    border-border-default
    z-50
    md:hidden
    flex
    items-center
    justify-between
    gap-4
    overflow-x-hidden
  `}
`;

const MobilePriceSummary = styled.div`
  ${tw`
    flex
    flex-col
  `}
`;

const MobilePriceLabel = styled.span`
  ${tw`
    text-xs
    text-text-secondary
  `}
`;

const MobilePriceValue = styled.span`
  ${tw`
    text-xl
    font-bold
  `}
  color: ${Colors.Black};
`;

const MobilePriceSublabel = styled.span`
  ${tw`
    text-xs
    text-text-tertiary
  `}
`;

const CarInfoWrapper = styled.div`
  ${tw`
    flex
    flex-col
    order-2
    mb-6
  `}
`;

const PriceAndCouponWrapper = styled.div`
  ${tw`
    flex
    flex-col
    order-4
  `}
`;

const CouponSection = styled.div`
  ${tw`
    mt-4
    p-4
    border
    border-border-default
    rounded-lg
  `}
`;

const CouponInputGroup = styled.div`
  ${tw`
    flex
    gap-2
  `}
`;

const CouponInput = styled.input`
  ${tw`
    flex-1
    px-3
    py-2
    border
    border-border-default
    rounded-md
    focus:outline-none
    focus:ring-2
    focus:ring-black
  `}
`;

const ApplyButton = styled.button`
  ${tw`
    px-4
    py-2
    text-white
    rounded-md
    transition-colors
  `}
  background-color: ${Colors.Black};
  &:hover {
    background-color: ${Colors.BlackHover};
  }
`;

const CouponError = styled.div`
  ${tw`
    mt-2
    text-sm
    text-red-600
  `}
`;

const CouponSuccess = styled.div`
  ${tw`
    mt-2
    text-sm
    text-green-600
  `}
`;

// Free Cancellation Section - Desktop version (shown in left section on md+)
const FreeCancellationSectionDesktop = styled.div`
  ${tw`
    hidden
    md:flex
    items-start
    gap-3
    p-4
    rounded-lg
    mb-4
  `}
  background-color: ${Colors.Gray100};
  border: 1px solid ${Colors.Gray200};
`;

// Free Cancellation Section - Mobile version (shown after coupon on <md)
const FreeCancellationSectionMobile = styled.div`
  ${tw`
    flex
    md:hidden
    items-start
    gap-3
    p-4
    rounded-lg
    mt-4
  `}
  background-color: ${Colors.Gray100};
  border: 1px solid ${Colors.Gray200};
`;

const FreeCancellationIcon = styled.div`
  ${tw`
    flex-shrink-0
    mt-0.5
  `}
  color: ${Colors.Black};
`;

const FreeCancellationText = styled.p`
  ${tw`
    text-sm
  `}
  color: ${Colors.TextPrimary};
`;

const MileageInfoSectionDesktop = styled.div`
  ${tw`hidden md:flex items-start gap-3 p-4 rounded-lg mb-4`}
  background-color: var(--color-bg-secondary);
  border: 1px solid var(--color-border-strong);
`;

const MileageInfoSectionMobile = styled.div`
  ${tw`flex md:hidden items-start gap-3 p-4 rounded-lg mt-4`}
  background-color: var(--color-bg-secondary);
  border: 1px solid var(--color-border-strong);
`;

const MileageInfoIcon = styled.div`
  ${tw`flex-shrink-0 mt-0.5`}
  color: var(--color-black);
`;

const MileageInfoText = styled.p`
  ${tw`text-sm`}
  color: ${Colors.TextPrimary};
`;

// Terms Agreement Section - Desktop version (shown in left section on md+)
const TermsSectionDesktop = styled.div`
  ${tw`
    hidden
    md:block
    mb-6
  `}
`;

// Terms Agreement Section - Mobile version (shown after coupon on <md)
const TermsSectionMobile = styled.div`
  ${tw`
    block
    md:hidden
    mt-4
  `}
`;

const TermsCheckboxContainer = styled.label<{ hasError?: boolean }>`
  ${tw`
    flex
    items-start
    gap-3
    cursor-pointer
    p-3
    rounded-lg
    transition-all
  `}
  background-color: ${Colors.BgSecondary};
  ${props => props.hasError && tw`border border-red-500 bg-red-50`}
`;

const TermsCheckbox = styled.div<{ checked: boolean }>`
  ${tw`
    flex-shrink-0
    w-5
    h-5
    rounded-md
    border-solid
    border-2
    flex
    items-center
    justify-center
    transition-all
    mt-0.5
  `}
  ${props =>
    props.checked
      ? css`
          background-color: ${Colors.Black};
          border-color: ${Colors.Black};
        `
      : css`
          border-color: var(--color-text-tertiary);

          background-color: white;
        `}
`;

const TermsCheckboxLabel = styled.span`
  ${tw`
    text-sm
    text-text-secondary
  `}
`;

const TermsLink = styled(Link)`
  ${tw`
    underline
  `}
  color: ${Colors.BlackLight};
  &:hover {
    color: ${Colors.Black};
  }
`;

const TermsError = styled.div`
  ${tw`
    text-sm
    text-red-600
    mt-2
    ml-8
  `}
`;

// Shake animation keyframes
const shakeAnimation = keyframes`
  0%, 100% { transform: translateX(0); }
  10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
  20%, 40%, 60%, 80% { transform: translateX(5px); }
`;

const TermsCheckboxContainerAnimated = styled(TermsCheckboxContainer)<{ isShaking?: boolean }>`
  ${props =>
    props.isShaking &&
    css`
      animation: ${shakeAnimation} 0.5s ease-in-out;
    `}
`;

const CarTitleRow = styled.div`
  ${tw`
    flex
    items-center
    justify-between
  `}
`;
const CarTitle = styled.h3`
  ${tw`
    text-lg
    font-semibold
  `}
`;

const CarRating = styled.div`
  ${tw`
    text-sm
    text-text-secondary
    flex
    items-center
    gap-1
  `}
`;

const CarDates = styled.div`
  ${tw`
    text-text-primary
    mt-1
  `}
`;

const formatDateTime = (timestamp: number) => {
  return format(new Date(timestamp), 'MMM dd yyyy HH:mm');
};

/** The driver age the trip was started with (navigation state, then the URL), else 25 as before. */
export function tripDriverAge(createBookingInput?: { driverAge?: unknown } | null): number {
  const fromState = Number(createBookingInput?.driverAge);
  if (Number.isFinite(fromState) && fromState > 0) return fromState;
  const fromUrl = Number(new URLSearchParams(window.location.search).get('driverAge'));
  if (Number.isFinite(fromUrl) && fromUrl > 0) return fromUrl;
  return 25;
}

const CheckoutPage = () => {
  const { stripePromise, publishableKey, ready: stripeReady } = useStripePromise();
  const navigate = useNavigate();

  const [clientSecret, setClientSecret] = useState<string | null>(null);
  // Spec 013 D1: lets the Payment Element show this brand's saved cards and the save box.
  const [customerSessionClientSecret, setCustomerSessionClientSecret] = useState<string | null>(
    null,
  );
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [paymentMethodId, setPaymentMethodId] = useState<string | null>(null);
  const [depositClientSecret, setDepositClientSecret] = useState<string | null>(null);
  const [rentalClientSecret, setRentalClientSecret] = useState<string | null>(null);
  const [paymentStep, setPaymentStep] = useState<'deposit' | 'rental' | 'verifying'>('deposit');
  const [couponCode, setCouponCode] = useState('');
  const [couponError, setCouponError] = useState('');
  const [couponSuccess, setCouponSuccess] = useState('');
  const [appliedCouponCode, setAppliedCouponCode] = useState<string | null>(null);
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [processingMessage, setProcessingMessage] = useState('Processing payment...');
  const [isPreparingPayment, setIsPreparingPayment] = useState(false);
  const [termsChecked, setTermsChecked] = useState(false);
  const [showTermsError, setShowTermsError] = useState(false);
  // Spec 023: "Bookings open soon" when the site is not taking bookings yet.
  const bookingsClosed = useBookingsClosedPrompt();
  const policies = useCheckoutPolicies('car');
  const [isTermsShaking, setIsTermsShaking] = useState(false);
  const termsDesktopRef = useRef<HTMLLabelElement>(null);
  const termsMobileRef = useRef<HTMLLabelElement>(null);
  const { user, isAuthenticated } = useSnapshot(authStore);

  const [cancelBooking, { loading: cancelBookingLoading }] = useMutation<
    ICancelBookingMutation,
    ICancelBookingMutationVariables
  >(CANCEL_BOOKING, {
    onCompleted: data => {
      toast.success('Booking cancelled successfully');
    },
  });

  const [createSetupIntent] = useMutation(CREATE_SETUP_INTENT_FOR_BOOKING, {
    onError: error => {
      // Spec 015: a car unlisted since the trip was created gets the server's sentence.
      toast.error(
        graphqlErrorCode(error) === CAR_NOT_BOOKABLE
          ? graphqlErrorMessage(error, 'This car cannot be booked right now.')
          : 'Setup failed: ' + error?.message,
        { autoClose: 8000 },
      );
    },
  });

  // Errors from these two are described by the caller (`describeCheckoutPaymentError`):
  // a card refused for its name needs a retry with another card, not a generic toast.
  const [createDepositPaymentIntent] = useMutation(CREATE_DEPOSIT_PAYMENT_INTENT);

  const [createRentalPaymentIntent] = useMutation(CREATE_RENTAL_PAYMENT_INTENT);

  const [updateProtectionPlan, { loading: updateProtectionPlanLoading }] =
    useUpdateBookingProtectionPlanMutation({
      refetchQueries: ['GetBooking'],
      onError: error => {},
    });

  const [createBookingIntent, { loading: createBookingIntentLoading }] =
    useCreateBookingIntentMutation({
      onCompleted: ({ createBookingIntent }) => {
        // Navigate to the new booking with coupon applied
        navigate(`/cars/guest/pay/${carId}/${createBookingIntent.id}`, {
          replace: true,
        });
      },
      onError: error => {
        toast.error('Failed to apply coupon: ' + error.message);
      },
    });

  const [checkBookingAvailability, { loading: checkBookingAvailabilityLoading }] =
    useCheckCarAvailabilityMutation();

  const [validateCoupon, { loading: validatingCoupon }] = useLazyQuery(VALIDATE_COUPON, {
    onCompleted: async data => {
      if (data.validateCoupon.isValid) {
        setCouponError('');
        setCouponSuccess('Coupon applied successfully!');
        const validatedCouponCode = couponCode.toUpperCase();
        setAppliedCouponCode(validatedCouponCode);
        setCouponCode('');

        // Recreate booking intent with coupon code to update the receipt
        if (bookingId && bookingIntentData?.booking && carData?.car) {
          const booking = bookingIntentData.booking;
          // Get hostId from booking.host or car.owner
          const hostId = booking.host?.id || carData?.car?.owner?.id;

          if (!hostId) {
            setCouponError('Unable to determine host ID. Please refresh the page.');
            setCouponSuccess('');
            return;
          }

          const createBookingInput: any = {
            carId,
            hostId: hostId, // Required field
            startAt: booking.startAt,
            endAt: booking.endAt,
            pickupLocationId: booking.pickupLocationId,
            returnLocationId: booking.returnLocationId,
            // Spec 014b (research R8): keep the trip's driver age; a hard-coded 25 let a
            // coupon rebuild pass the brand's age check for a younger driver.
            driverAge: tripDriverAge(location?.state?.createBookingInput),
            couponCode: validatedCouponCode, // Add coupon code
          };
          const referrerMarker = getReferralMarkerForBooking();
          if (referrerMarker) {
            createBookingInput.referrer = referrerMarker;
          }

          // Include protection plan if available
          if (booking.protectionPlan) {
            createBookingInput.protectionPlan = booking.protectionPlan;
            if (booking.protectionPlanId) {
              createBookingInput.protectionPlanId = booking.protectionPlanId;
            }
          }

          // Recreate booking intent with coupon
          createBookingIntent({
            variables: { createBookingInput },
          });
        } else {
          setCouponError('Booking or car information not available. Please refresh the page.');
          setCouponSuccess('');
        }
      } else {
        setCouponError(data.validateCoupon.errorMessage || 'Invalid coupon');
        setCouponSuccess('');
        setAppliedCouponCode(null);
      }
    },
    onError: error => {
      setCouponError(error.message || 'Failed to validate coupon');
      setCouponSuccess('');
      setAppliedCouponCode(null);
    },
  });

  const handleApplyCoupon = () => {
    if (!couponCode.trim()) {
      setCouponError('Please enter a coupon code');
      return;
    }

    if (!carId || !bookingIntentData?.booking) {
      setCouponError('Booking information not available');
      return;
    }

    const booking = bookingIntentData.booking;
    const startAt = booking.startAt;
    const endAt = booking.endAt;
    const totalDays = booking.receipt?.totalDays || 0;
    const guestId = authStore.user?.id;

    validateCoupon({
      variables: {
        code: couponCode.toUpperCase(),
        carId,
        guestId,
        startAt,
        endAt,
        totalDays,
      },
    });
  };

  const { id: carId } = useParams<{ id: string }>();
  const { bookingId } = useParams<{ bookingId: string }>();
  // Spec 028: the trip's unit and currency.
  const bookingLocale = useBookingLocale(bookingId, 'guest');
  const location = useLocation();
  const createBookingInput = location?.state?.createBookingInput;

  const {
    data: storedCarData,
    loading: carLoading,
    error: carError,
  } = useQuery<IGetCarQuery>(GET_CAR, {
    variables: { id: carId },
    fetchPolicy: 'cache-first',
    skip: !carId,
  });
  // Spec 014b: the car as this site's brand rents it.
  const carData = useMemo(
    () =>
      storedCarData ? { ...storedCarData, car: carAsRented(storedCarData.car) } : storedCarData,
    [storedCarData],
  );

  const {
    data: bookingIntentData,
    loading: bookingIntentLoading,
    refetch: refetchBooking,
  } = useGetBookingQuery({
    variables: { id: bookingId ?? '' },
    skip: !bookingId,
    onError: error => {
      toastActions.showToastAlert(
        'Booking not found',
        'Booking not found' + error?.message,
        'navigate to home page',
        () => {
          navigate('/');
        },
        () => {
          navigate('/');
        },
      );
    },
  });

  // Query pickup location if it's different from car's location (e.g., airport)
  const pickupLocationId = bookingIntentData?.booking?.pickupLocationId;
  const [pickupLocationData, setPickupLocationData] = useState<
    IGetLocationQuery['location'] | null
  >(null);
  const [getPickupLocation] = useLazyQuery<IGetLocationQuery, IGetLocationQueryVariables>(
    GET_LOCATION,
    {
      onCompleted: data => {
        if (data?.location) {
          setPickupLocationData(data.location);
        }
      },
    },
  );

  // Fetch pickup location if it exists and is different from car's location
  useEffect(() => {
    if (pickupLocationId && carData?.car?.location?.id) {
      // Only fetch if pickup location is different from car's location
      if (pickupLocationId !== carData.car.location.id) {
        getPickupLocation({ variables: { id: pickupLocationId } });
      } else {
        setPickupLocationData(null);
      }
    }
  }, [pickupLocationId, carData?.car?.location?.id, getPickupLocation]);

  // Calculate free cancellation date (24 hours before trip start)
  const freeCancellationDate = useMemo(() => {
    const startAt = createBookingInput?.startAt || bookingIntentData?.booking?.startAt;
    if (!startAt) return null;

    // startAt is a Unix timestamp in milliseconds
    const startTimestamp = Number(startAt);
    if (isNaN(startTimestamp) || startTimestamp <= 0) return null;

    // Free cancellation is 24 hours before trip start
    const cancellationDeadline = new Date(startTimestamp - 24 * 60 * 60 * 1000);
    const now = new Date();

    // If we're past the deadline, still show the date but user won't get free cancellation
    // (The UI shows this as informational)
    return format(cancellationDeadline, 'MMM d, yyyy, h:mm a');
  }, [createBookingInput?.startAt, bookingIntentData?.booking?.startAt]);

  // Function to scroll to terms and shake
  const scrollToTermsAndShake = useCallback(() => {
    // Determine which ref to use based on screen size
    const isMobile = window.innerWidth < 768;
    const targetRef = isMobile ? termsMobileRef : termsDesktopRef;

    if (targetRef.current) {
      // Scroll to the element with some offset
      targetRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    // Trigger shake animation
    setIsTermsShaking(true);
    setTimeout(() => {
      setIsTermsShaking(false);
    }, 500);
  }, []);

  const handleMakePayment = async (skipPhoneCheck = false) => {
    try {
      // Check terms agreement first
      if (!termsChecked) {
        setShowTermsError(true);
        toast.error('Please agree to the terms and conditions before proceeding');
        // Scroll to terms and shake
        setTimeout(() => scrollToTermsAndShake(), 100);
        return;
      }

      if (!isAuthenticated || !user) {
        modalActions.openSignInModal({
          pendingAction: () => handleMakePayment(skipPhoneCheck),
        });
        return;
      }

      // Skip phone verification check if called from verification callback
      if (!skipPhoneCheck && !user?.isPhoneVerified) {
        setShowPhoneModal(true);
        return;
      }

      if (!bookingId) throw new Error('missing booking ID');
      if (!bookingIntentData?.booking?.receipt?.totalPrice) {
        throw new Error('total price is not correct, please try again later');
      }

      // Show loading overlay while preparing payment
      setIsPreparingPayment(true);
      setProcessingMessage('Preparing payment form...');

      try {
        // Spec 031: record the versions on screen, then the bookings-open check, then the card.
        await policies.accept(bookingId);
        const bookingsOpen = await bookingsClosed.beforeConfirm();
        if (!bookingsOpen) return;
        // Spec 013 D2: every checkout starts with a SetupIntent, with or without a deposit.
        // The server then checks the card is the guest's own (name and ownership) before
        // it charges the deposit and the rental, and keeps the card for this trip's
        // later charges. The old deposit-free path charged from the browser, before the
        // server could see the card.
        const { data: setupIntentData } = await createSetupIntent({
          variables: { bookingId },
        });

        if (!setupIntentData?.createSetupIntentForBooking?.clientSecret) {
          throw new Error('Failed to create setup intent');
        }

        setClientSecret(setupIntentData.createSetupIntentForBooking.clientSecret);
        setCustomerSessionClientSecret(
          setupIntentData.createSetupIntentForBooking.customerSessionClientSecret ?? null,
        );
        setPaymentStep('deposit');
        setShowPaymentForm(true);
      } finally {
        // Hide loading overlay once payment form is ready
        setIsPreparingPayment(false);
      }
    } catch (error) {
      // todo: add a log tracker here
      setIsPreparingPayment(false);
      if (isPolicyVersionOutdated(error)) {
        setTermsChecked(false);
        setShowTermsError(true);
        toast.error(outdatedPolicyMessage(error));
        return;
      }
      if (isAuthRequiredError(error)) {
        modalActions.openSignInModal({
          pendingAction: () => handleMakePayment(skipPhoneCheck),
        });
        return;
      }
      if (error instanceof Error) {
        toast.error('Error making payment:' + error?.message);
      } else {
        toast.error('Payment failed');
      }
    }
  };

  const onPaymentFailedCallback = (error: any) => {
    if (!bookingId) return;
    // for some reason during beforeTransactionFinish the payment fails or the card had been declined
    // in this case we need to show a message to the user the reason of the failure
    if (error instanceof Error) {
      toast.error(error.message);
    } else {
      // todo: add a log tracker here
      toast.error('Payment failed');
    }
    setShowPaymentForm(false);
    setIsProcessingPayment(false);
  };

  const handleRentalPayment = async (paymentMethodId: string) => {
    try {
      if (!bookingId) throw new Error('Missing booking ID');

      setProcessingMessage('Processing rental payment...');

      // Create rental payment intent (off-session, confirmed automatically)
      const { data: rentalData } = await createRentalPaymentIntent({
        variables: {
          input: {
            bookingId,
            paymentMethodId,
          },
        },
      });

      if (!rentalData?.createRentalPaymentIntent) {
        throw new Error('Failed to create rental payment intent');
      }

      const rental = rentalData.createRentalPaymentIntent;
      const confirmedBooking = rental.booking;
      if (confirmedBooking?.status === BookingStatus.Booked) {
        setIsProcessingPayment(false);
        setShowPaymentForm(false);
        navigate('/guest/mybookings', { replace: true });
        setTimeout(() => {
          navigate(`/cars/guest/booking/${confirmedBooking.id || bookingId}`);
        }, 100);
        return;
      }

      // The rental intent is confirmed with the guest present (spec 013 D2); a bank
      // challenge comes back here and the guest completes it before verification.
      if (rental.requiresAction && rental.clientSecret) {
        setProcessingMessage('Your bank asks you to confirm this payment...');
        await completeBankChallenge(rental.clientSecret);
      }

      setPaymentStep('verifying');
      onPaymentSuccessCallback({
        paymentIntent: { id: rental.paymentIntentId },
      });
    } catch (error) {
      const described = describeCheckoutPaymentError(
        error,
        'Rental payment failed. Your deposit will be automatically refunded.',
      );
      if (described.bookingsClosed) {
        bookingsClosed.show(described.message);
      } else {
        toast.error(described.message, { autoClose: 8000 });
      }
      setShowPaymentForm(false);
      setIsProcessingPayment(false);
      if (described.retryWithAnotherCard) {
        // The refused card was detached; a new SetupIntent lets the guest enter another.
        void handleMakePayment(true);
      }
      // Otherwise the backend webhook auto-refunds the deposit
    }
  };

  /** Runs the 3DS challenge for a deposit or rental intent the server confirmed on-session. */
  const completeBankChallenge = async (intentClientSecret: string) => {
    const stripe = await stripePromise;
    if (!stripe) throw new Error('Payments are not ready yet. Please try again.');
    const { error, paymentIntent } = await stripe.confirmCardPayment(intentClientSecret);
    if (error) throw new Error(error.message || 'The bank did not approve this payment.');
    if (
      paymentIntent?.status !== 'succeeded' &&
      paymentIntent?.status !== 'requires_capture' &&
      paymentIntent?.status !== 'processing'
    ) {
      throw new Error('The payment was not completed. Please try again.');
    }
  };

  const [verifyBookingPayment] = useMutation(gql`
    mutation VerifyBookingPayment($bookingId: String!, $paymentIntentId: String) {
      verifyBookingPayment(bookingId: $bookingId, paymentIntentId: $paymentIntentId) {
        id
        status
      }
    }
  `);

  const onPaymentSuccessCallback = async (result?: any) => {
    if (!bookingId) return toast.error('missing booking ID');

    const depositAmount = bookingIntentData?.booking?.receipt?.securityDeposit || 0;

    // SETUP-FIRST FLOW (spec 013 D2): a SetupIntent result means the card was just
    // collected; the server now charges the deposit (when there is one) and the rental.
    if (result?.setupIntent) {
      // Step 1: User entered card, now extract payment method and create both deposit + rental PIs
      try {
        // Extract payment method ID from setupIntent or paymentIntent result
        // Start with any payment method we already stored (from prior step)
        let savedPaymentMethodId = paymentMethodId;

        if (result?.setupIntent) {
          // SetupIntent can have payment_method as string or object
          savedPaymentMethodId =
            typeof result.setupIntent.payment_method === 'string'
              ? result.setupIntent.payment_method
              : result.setupIntent.payment_method?.id;
        } else if (result?.paymentIntent) {
          savedPaymentMethodId =
            typeof result.paymentIntent.payment_method === 'string'
              ? result.paymentIntent.payment_method
              : result.paymentIntent.payment_method?.id;
        }

        if (!savedPaymentMethodId) {
          throw new Error('No payment method found after card entry');
        }

        setPaymentMethodId(savedPaymentMethodId);
        setPaymentStep('rental');
        setShowPaymentForm(false);

        // Show loading overlay
        setIsProcessingPayment(true);

        if (depositAmount > 0) {
          setProcessingMessage(`Authorizing $${depositAmount} security deposit...`);

          // Confirmed server-side with the guest present; a bank challenge comes back
          // as requiresAction and is completed here before the rental is charged.
          const { data: depositData } = await createDepositPaymentIntent({
            variables: {
              input: {
                bookingId,
                paymentMethodId: savedPaymentMethodId,
              },
            },
          });

          const deposit = depositData?.createDepositPaymentIntent;
          if (!deposit) {
            throw new Error('Failed to create deposit payment intent');
          }
          if (deposit.requiresAction && deposit.clientSecret) {
            setProcessingMessage('Your bank asks you to confirm the deposit...');
            await completeBankChallenge(deposit.clientSecret);
          }

          setProcessingMessage('Security deposit authorized. Processing rental payment...');
        } else {
          setProcessingMessage('Processing rental payment...');
        }
        await handleRentalPayment(savedPaymentMethodId);
      } catch (error: any) {
        const described = describeCheckoutPaymentError(error, 'Deposit payment failed');
        if (described.bookingsClosed) {
          bookingsClosed.show(described.message);
        } else {
          toast.error(described.message, { autoClose: 8000 });
        }
        setShowPaymentForm(false);
        setIsProcessingPayment(false);
        if (described.retryWithAnotherCard) {
          void handleMakePayment(true);
        }
      }
    } else {
      // Step 2: Rental payment succeeded OR legacy flow (no deposit)
      setShowPaymentForm(false);
      setPaymentStep('verifying');
      setIsProcessingPayment(true);
      setProcessingMessage('Payment successful, verifying booking...');

      // Extract paymentIntentId if available
      const paymentIntentId = result?.paymentIntent?.id;

      // Webhook/client verification fallback. Deposit-first normally returns BOOKED
      // from createRentalPaymentIntent now, so keep this short.
      let attempts = 0;
      const maxAttempts = 2;

      const pollBookingStatus = async () => {
        try {
          const { data } = await refetchBooking();
          // Check if booking status is BOOKED
          if (data?.booking?.status === BookingStatus.Booked) {
            // D20: only the first paid booking after this visit's redirect records the fee.
            clearReferralMarker();
            // Success! Close overlay and navigate
            setIsProcessingPayment(false);
            navigate('/guest/mybookings', { replace: true });
            setTimeout(() => {
              navigate(`/cars/guest/booking/${bookingId}`);
            }, 100);
            return;
          }
        } catch (error) {}

        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(pollBookingStatus, 1000);
        } else {
          // Polling failed, try FORCE VERIFY
          try {
            setProcessingMessage('Finalizing booking confirmation...');
            const verifyVariables: any = { bookingId };
            if (paymentIntentId) {
              verifyVariables.paymentIntentId = paymentIntentId;
            }

            const result = await verifyBookingPayment({
              variables: verifyVariables,
            });

            if (result.data?.verifyBookingPayment?.status === BookingStatus.Booked) {
              setIsProcessingPayment(false);
              navigate('/guest/mybookings', { replace: true });
              setTimeout(() => {
                navigate(`/cars/guest/booking/${bookingId}`);
              }, 100);
            } else {
              // Still failed even after force verify?
              setIsProcessingPayment(false);
              toast.error(
                'Payment successful but booking confirmation pending. Please contact support.',
              );
            }
          } catch (e) {
            setIsProcessingPayment(false);
            toast.error(
              'Payment successful but booking confirmation pending. Please contact support.',
            );
          }
        }
      };

      pollBookingStatus();
    }
  };

  const setAndUpdateSelectedPlan = (plan: IProtectionPlan) => {
    if (!bookingId) return toast.error('missing booking ID');
    updateProtectionPlan({
      variables: {
        input: {
          bookingId,
          protectionPlanId: plan.id,
          protectionPlan: plan.plan,
        },
      },
    });
  };

  if (bookingIntentLoading || isPreparingPayment) {
    return <CheckoutPageSkeleton />;
  }

  const renderPaymentSection = () => {
    // Always pass clientSecret when available (SetupIntent or PaymentIntent)
    const elementsOptions = clientSecret
      ? {
          clientSecret,
          // Saved cards and the "save this card" box come from the CustomerSession
          // (spec 013 D1). Without it the form still works, just without saved cards.
          ...(customerSessionClientSecret ? { customerSessionClientSecret } : {}),
          appearance: {
            theme: 'stripe' as const,
            variables: {
              spacingUnit: '4px',
              borderRadius: '8px',
            },
          },
        }
      : undefined;

    if (!elementsOptions) return null; // Don't render if no clientSecret
    if (!stripeReady) return null; // Wait for the brand's Stripe key; Elements cannot swap it later

    // Elements cannot change `clientSecret` after it mounts. A retry after a failed
    // deposit or rental creates a new SetupIntent, so remount on a new secret;
    // otherwise Stripe confirms the old, already succeeded one
    // (`setup_intent_unexpected_state`).
    return (
      <Elements
        stripe={stripePromise}
        options={elementsOptions}
        key={`${publishableKey}:${clientSecret}`}
      >
        <PaymentForm
          isOpen={showPaymentForm}
          clientSecret={clientSecret || undefined}
          beforeTransactionFinish={async () => {
            if (!carId)
              return new Promise((resolve, reject) => reject(new Error('missing car ID')));
            if (!bookingId)
              return new Promise((resolve, reject) => reject(new Error('missing booking ID')));
            const { data: checkBookingAvailabilityData } = await checkBookingAvailability({
              variables: {
                carId,
                startAt: createBookingInput?.startAt || bookingIntentData?.booking?.startAt,
                endAt: createBookingInput?.endAt || bookingIntentData?.booking?.endAt,
                // NEVER PASS BOOKINGID HERE, we cannot pass the bookingId here because the booking is not confirmed yet and if we pass it, a user can always rebook the trip again and again.
              },
            });
            if (!checkBookingAvailabilityData?.checkCarAvailability?.isAvailable) {
              return new Promise((resolve, reject) =>
                reject(new Error('This car is no more available for the selected dates')),
              );
            }
          }}
          // Spec 023: asked when Pay is pressed, so a site closed after the page opened still stops.
          beforeConfirm={bookingsClosed.beforeConfirm}
          onClose={() => setShowPaymentForm(false)}
          onPaymentSuccessCallback={onPaymentSuccessCallback}
          onPaymentFailedCallback={onPaymentFailedCallback}
        />
      </Elements>
    );
  };

  return (
    <>
      <PageContainer data-testid="checkout-page-container">
        <LeftSection>
          <CheckoutTitle data-testid="checkout-title">Checkout</CheckoutTitle>

          <ProtectionSection data-testid="checkout-protection-section">
            <ProtectionTitle>Protection options</ProtectionTitle>
            <ProtectionDescription>{getBrand().copy.protectionDescription}</ProtectionDescription>
            <ChooseProtectionPlan
              onSelectedPlan={setAndUpdateSelectedPlan}
              rentalType={bookingIntentData?.booking?.receipt?.rentalType}
            />
          </ProtectionSection>

          {/* Free Cancellation Section - Desktop (md+) */}
          {freeCancellationDate && (
            <FreeCancellationSectionDesktop data-testid="free-cancellation-desktop">
              <FreeCancellationIcon>
                <FontAwesomeIcon icon={faCalendarAlt} />
              </FreeCancellationIcon>
              <FreeCancellationText>
                Free cancellation before {freeCancellationDate}—if plans change, we've got your
                back.
              </FreeCancellationText>
            </FreeCancellationSectionDesktop>
          )}

          {/* Mileage Info - Desktop (md+) */}
          {bookingIntentData?.booking?.receipt?.allowedMileage != null && (
            <MileageInfoSectionDesktop>
              <MileageInfoIcon>
                <FontAwesomeIcon icon={faRoad} />
              </MileageInfoIcon>
              <MileageInfoText>
                {checkoutAllowanceText(
                  bookingIntentData.booking.receipt.allowedMileage,
                  bookingLocale.distanceUnit,
                )}
              </MileageInfoText>
            </MileageInfoSectionDesktop>
          )}

          {/* Terms Agreement Section - Desktop (md+) */}
          <TermsSectionDesktop>
            <TermsCheckboxContainerAnimated
              ref={termsDesktopRef}
              hasError={showTermsError}
              isShaking={isTermsShaking}
              data-testid="terms-agreement-desktop"
              role="checkbox"
              aria-checked={termsChecked}
              tabIndex={0}
              onClick={() => {
                setTermsChecked(!termsChecked);
                setShowTermsError(false);
                policies.clearOutdated();
              }}
              onKeyDown={event => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  setTermsChecked(!termsChecked);
                  setShowTermsError(false);
                  policies.clearOutdated();
                }
              }}
            >
              <TermsCheckbox checked={termsChecked}>
                {termsChecked && (
                  <FontAwesomeIcon icon={faCheck} style={{ color: 'white', fontSize: '12px' }} />
                )}
              </TermsCheckbox>
              <TermsCheckboxLabel>
                I agree to pay the total shown and to the{' '}
                <TermsLink to={policies.rental.href} onClick={e => e.stopPropagation()}>
                  rental agreement
                </TermsLink>
                ,{' '}
                <TermsLink to="/policies/terms" onClick={e => e.stopPropagation()}>
                  terms of service
                </TermsLink>
                ,{' '}
                <TermsLink to="/policies/cancellation" onClick={e => e.stopPropagation()}>
                  cancellation policy
                </TermsLink>{' '}
                and I acknowledge the{' '}
                <TermsLink to="/policies/privacy" onClick={e => e.stopPropagation()}>
                  privacy policy
                </TermsLink>
              </TermsCheckboxLabel>
            </TermsCheckboxContainerAnimated>
            {policies.outdatedMessage && <TermsError>{policies.outdatedMessage}</TermsError>}
            {showTermsError && (
              <TermsError>Please agree to the terms and conditions before proceeding</TermsError>
            )}
          </TermsSectionDesktop>
        </LeftSection>

        <RightSection>
          <CarInfoWrapper>
            <CarImage
              src={
                carData?.car?.thumbnailUrl
                  ? `${URLS.S3_BUCKET.CAR_THUMBNAILS}/${carData?.car?.thumbnailUrl}`
                  : `${URLS.S3_BUCKET.CAR_THUMBNAILS}/car-images/default-car.jpg`
              }
              alt={carData?.car?.name || 'Car image'}
            />
            <CarSummary>
              <CarDetails>
                <CarTitleRow>
                  <CarTitle>{carData?.car?.name}</CarTitle>
                  <CarRating>5.0 ★</CarRating>
                </CarTitleRow>
                {bookingIntentData?.booking?.receipt?.rentalType === 'MONTHLY' && (
                  <span
                    style={{
                      display: 'inline-block',
                      background: 'var(--color-black)',
                      color: 'white',
                      padding: '2px 8px',
                      borderRadius: '12px',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      marginTop: '4px',
                      letterSpacing: '0.5px',
                    }}
                    data-testid="monthly-rental-badge"
                  >
                    MONTHLY RENTAL
                  </span>
                )}
                <CarDates>
                  {formatDateTime(
                    createBookingInput?.startAt || bookingIntentData?.booking?.startAt,
                  )}{' '}
                  → {formatDateTime(createBookingInput?.endAt || bookingIntentData?.booking?.endAt)}
                </CarDates>
              </CarDetails>
            </CarSummary>

            <LocationInfo>
              <FontAwesomeIcon icon={faMapMarkerAlt} />
              <span>
                {pickupLocationData
                  ? pickupLocationData.name
                    ? pickupLocationData.name
                    : pickupLocationData.city
                  : carData?.car?.location
                    ? carData.car.location.city
                    : 'Location not available'}
              </span>
            </LocationInfo>
          </CarInfoWrapper>

          <PriceAndCouponWrapper>
            {bookingIntentData?.booking?.receipt && (
              <ChargingDetails
                receipt={bookingIntentData?.booking?.receipt}
                currency={bookingLocale.currency}
                taxLabel={bookingLocale.taxLabel}
              />
            )}

            <CouponSection data-testid="checkout-coupon-section">
              <div style={{ marginBottom: '0.5rem', fontWeight: '600' }}>Have a coupon code?</div>
              {appliedCouponCode ? (
                <CouponSuccess data-testid="checkout-coupon-success">
                  Coupon {appliedCouponCode} applied successfully!
                </CouponSuccess>
              ) : (
                <>
                  <CouponInputGroup>
                    <CouponInput
                      type="text"
                      value={couponCode}
                      onChange={e => {
                        setCouponCode(e.target.value.toUpperCase());
                        setCouponError('');
                        setCouponSuccess('');
                      }}
                      placeholder="Enter coupon code"
                      maxLength={7}
                      data-testid="checkout-coupon-input"
                    />
                    <ApplyButton
                      onClick={handleApplyCoupon}
                      disabled={validatingCoupon || !couponCode.trim()}
                      data-testid="checkout-coupon-apply-button"
                    >
                      {validatingCoupon ? 'Applying...' : 'Apply'}
                    </ApplyButton>
                  </CouponInputGroup>
                  {couponError && (
                    <CouponError data-testid="checkout-coupon-error">{couponError}</CouponError>
                  )}
                  {couponSuccess && <CouponSuccess>{couponSuccess}</CouponSuccess>}
                </>
              )}
            </CouponSection>

            {/* Free Cancellation Section - Mobile (<md) */}
            {freeCancellationDate && (
              <FreeCancellationSectionMobile>
                <FreeCancellationIcon>
                  <FontAwesomeIcon icon={faCalendarAlt} />
                </FreeCancellationIcon>
                <FreeCancellationText>
                  Free cancellation before {freeCancellationDate}—if plans change, we've got your
                  back.
                </FreeCancellationText>
              </FreeCancellationSectionMobile>
            )}

            {/* Mileage Info - Mobile (<md) */}
            {bookingIntentData?.booking?.receipt?.allowedMileage != null && (
              <MileageInfoSectionMobile>
                <MileageInfoIcon>
                  <FontAwesomeIcon icon={faRoad} />
                </MileageInfoIcon>
                <MileageInfoText>
                  {checkoutAllowanceText(
                    bookingIntentData.booking.receipt.allowedMileage,
                    bookingLocale.distanceUnit,
                  )}
                </MileageInfoText>
              </MileageInfoSectionMobile>
            )}

            {/* Terms Agreement Section - Mobile (<md) */}
            <TermsSectionMobile>
              <TermsCheckboxContainerAnimated
                ref={termsMobileRef}
                hasError={showTermsError}
                isShaking={isTermsShaking}
                data-testid="terms-agreement-mobile"
                role="checkbox"
                aria-checked={termsChecked}
                tabIndex={0}
                onClick={() => {
                  setTermsChecked(!termsChecked);
                  setShowTermsError(false);
                  policies.clearOutdated();
                }}
                onKeyDown={event => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    setTermsChecked(!termsChecked);
                    setShowTermsError(false);
                    policies.clearOutdated();
                  }
                }}
              >
                <TermsCheckbox checked={termsChecked}>
                  {termsChecked && (
                    <FontAwesomeIcon icon={faCheck} style={{ color: 'white', fontSize: '12px' }} />
                  )}
                </TermsCheckbox>
                <TermsCheckboxLabel>
                  I agree to pay the total shown and to the{' '}
                  <TermsLink to={policies.rental.href} onClick={e => e.stopPropagation()}>
                    rental agreement
                  </TermsLink>
                  ,{' '}
                  <TermsLink to="/policies/terms" onClick={e => e.stopPropagation()}>
                    terms of service
                  </TermsLink>
                  ,{' '}
                  <TermsLink to="/policies/cancellation" onClick={e => e.stopPropagation()}>
                    cancellation policy
                  </TermsLink>{' '}
                  and I acknowledge the{' '}
                  <TermsLink to="/policies/privacy" onClick={e => e.stopPropagation()}>
                    privacy policy
                  </TermsLink>
                </TermsCheckboxLabel>
              </TermsCheckboxContainerAnimated>
              {policies.outdatedMessage && <TermsError>{policies.outdatedMessage}</TermsError>}
              {showTermsError && (
                <TermsError>Please agree to the terms and conditions before proceeding</TermsError>
              )}
            </TermsSectionMobile>

            <DesktopBookButton
              onClick={() => handleMakePayment()}
              data-testid="checkout-book-button-desktop"
            >
              Book this trip
            </DesktopBookButton>
          </PriceAndCouponWrapper>
        </RightSection>
        <BookingsClosedModal message={bookingsClosed.message} onClose={bookingsClosed.close} />
        {showPhoneModal && (
          <PhoneVerification
            onClose={() => setShowPhoneModal(false)}
            onVerified={() => {
              // Skip phone check since we just verified - pass true to bypass the check
              handleMakePayment(true);
            }}
            initialPhoneNumber={user?.phoneNumber || ''}
          />
        )}
      </PageContainer>
      <FixedButtonContainer>
        <MobilePriceSummary>
          <MobilePriceLabel>total:</MobilePriceLabel>
          <MobilePriceValue>
            ${bookingIntentData?.booking?.receipt?.totalPrice?.toFixed(2) || '0.00'}
            {currencyCodeSuffix(bookingLocale.currency)}
          </MobilePriceValue>
          <MobilePriceSublabel>After taxes and fees</MobilePriceSublabel>
        </MobilePriceSummary>
        <div style={{ flex: 1, maxWidth: '200px' }}>
          <BookButton onClick={() => handleMakePayment()} data-testid="checkout-book-button-mobile">
            Book Now
          </BookButton>
        </div>
      </FixedButtonContainer>
      {renderPaymentSection()}

      {/* Loading Overlay during payment preparation */}
      <LoadingOverlay
        isOpen={isPreparingPayment}
        message={processingMessage}
        subtext="Please wait..."
        color={Colors.Black}
        iconSize={60}
        rotating={true}
      />

      {/* Loading Overlay during payment processing */}
      <LoadingOverlay
        isOpen={isProcessingPayment}
        message={processingMessage}
        subtext="Please do not close this page"
        color={Colors.Black}
        iconSize={60}
        rotating={true}
      />
    </>
  );
};

export default CheckoutPage;
